import { toast } from 'sonner'
import { useForm } from 'react-hook-form'

import { Car, StorePersistKeys } from 'src/types'
import { useLocales } from 'src/contexts'
import { TIME_IN_MS } from 'src/constants'

import { useManageUserCars } from 'src/hooks/useManageUserCars'
import { useAppSelector } from 'src/hooks/useAppSelector'

import { CarEditModalWindowProps } from 'src/features/CarEditModalWindow/types'
import { getFieldsForEditCarInfo } from 'src/features/CarEditModalWindow/helpers'
import { queryClient } from 'src/features/AppProviders/constants'

export const useCarEditModalWindow = ({
  carModel,
  carBrand,
  carNumber,
  vin,
  mileage,
  engineType,
  year,
  isActive,
  nextMaintenance,
  lastServiceDate,
  toggle,
}: Omit<CarEditModalWindowProps, 'isVisible'>) => {
  const { translations } = useLocales()

  const { distanceMetric } = useAppSelector(state => state.user)

  const { updateCar, isLoadingGeneral } = useManageUserCars()

  const { handleSubmit, control, getValues } = useForm({
    defaultValues: {
      carModel,
      carBrand,
      carNumber,
      vin: vin || '???',
      mileage: mileage || '???',
      engineType,
      year,
    },
  })

  const getUpdatedData = (): Car => {
    const { carModel, carBrand, carNumber, vin, mileage, engineType, year } =
      getValues()

    return {
      carNumber,
      carBrand,
      carModel,
      vin,
      mileage: Number(mileage) || 0,
      engineType,
      year,
      lastServiceDate: new Date(
        (lastServiceDate?.seconds || 0) * TIME_IN_MS.SECOND,
      ),
      nextMaintenance: new Date(
        (nextMaintenance?.seconds || 0) * TIME_IN_MS.SECOND,
      ),
      isActive,
    }
  }

  const onUpdate = (): void => {
    const newData = getUpdatedData()

    updateCar(
      {
        carNumber,
        newData,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [StorePersistKeys.USER_CARS],
          })
          toggle()
          toast(translations.trackFuel.historyFuel.toast.success.title, {
            description:
              translations.trackFuel.historyFuel.toast.success.description,
            action: {
              label:
                translations.trackFuel.historyFuel.toast.success.action.label,
              onClick: () => toast.dismiss(),
            },
          })
        },
        onError: err => {
          toast(translations.trackFuel.historyFuel.toast.error.title, {
            description:
              translations.trackFuel.historyFuel.toast.error.description + err,
            action: {
              label:
                translations.trackFuel.historyFuel.toast.error.action.label,
              onClick: () => toast.dismiss(),
            },
          })
        },
      },
    )
  }

  return {
    cancel: translations.trackFuel.historyFuel.alert.cancel,
    updateBtn: translations.trackFuel.historyFuel.alert.update.updateBtn,
    title: translations.myCars.editCardTitle,
    control,
    handleSubmit,
    onUpdate,
    isLoadingGeneral,
    fields: getFieldsForEditCarInfo(translations.userData, distanceMetric),
  }
}
